import Axios, { AxiosResponse } from 'axios';
import * as AccountRequest from '@/models/accountRequest';
import * as AccountResponse from '@/models/accountResponse';
import { Person } from '@/models/person';
import { Subject } from '@/models/subject';
import { Tag } from '@/models/tag';
import { UserTypes } from '@/enums/userTypes';
import { GradeYears } from '@/enums/gradeYears';
import { GenderTypes, getGenderTypeName } from '@/enums/genderTypes';
import { SchoolTypes } from '@/enums/schoolTypes';
import { FunctionTypes } from '@/enums/functionTypes';

export type Student = AccountResponse.StudentAccountDetailResponse;

export interface SearchCondition {
  ordinarySchoolId: number | string;
  gradeYear: GradeYears | string;
  tutorList: string[];
  tagList: Tag[];
  keyWord: string;
  creditcardRegistered: boolean | null;
  perPage: number;
  page: number;
  sort: string;
  schoolName: string;
  studentIdList: string[];
}

interface State {
  loading: boolean;
  countAll: number;
  students: Student[];
  searchCondition: SearchCondition;
  selectedStudent: Student | null;
}
const mapState: State = {
  loading: false,
  countAll: 0,
  students: new Array<Student>(),
  searchCondition: {
    ordinarySchoolId: '',
    gradeYear: '',
    tutorList: new Array<string>(),
    tagList: new Array<Tag>(),
    keyWord: '',
    creditcardRegistered: null,
    perPage: 20,
    page: 1,
    sort: '',
    schoolName : '',
    studentIdList: new Array<string>(),
  },
  selectedStudent: null,
};

const mapActions = {
  async searchStudents({ dispatch, commit }: any, searchCondition: SearchCondition) {
    commit('setLoading', true);
    const data = await dispatch('getStudents', searchCondition).catch((error: any) => {
      commit('setLoading', false);
      throw error;
    });
    commit('searchStudents', data);
  },
  async getSelectableStudents({ commit }: any, searchCondition: SearchCondition) {
    const request: AccountRequest.SelectableStudentAccountRequest = {
      tutorIdList: searchCondition.tutorList,
      ordinarySchoolId: searchCondition.ordinarySchoolId === ''
        ? undefined : searchCondition.ordinarySchoolId as string,
      gradeYear: searchCondition.gradeYear === ''
        ? undefined : searchCondition.gradeYear as number,
      tagList: searchCondition.tagList,
      keyWord: searchCondition.keyWord,
      creditcardRegistered: searchCondition.creditcardRegistered === null
        ? undefined : searchCondition.creditcardRegistered,
    };
    const response = await Axios.post('/api/Account/SelectableStudentSearch', request).catch((error) => {
      throw error;
    });
    response.data.accountList.forEach((account: any) => {
      if (typeof account.birthDay === 'string') {
        account.birthDay = new Date(account.birthDay + 'Z');
      }
    });
    return response.data;
  },
  async getStudents({ commit }: any, searchCondition: SearchCondition) {
    const request: AccountRequest.StudentAccountRequest = {
      id: undefined,
      userId: undefined,
      lastName: undefined,
      firstName: undefined,
      lastNameKana: undefined,
      firstNameKana: undefined,
      genderType: undefined,
      birthDay: undefined,
      postalCode: undefined,
      address: undefined,
      phone: undefined,
      note: undefined,
      tutorIdList: searchCondition.tutorList,
      ordinarySchoolId: searchCondition.ordinarySchoolId === ''
        ? undefined : searchCondition.ordinarySchoolId as string,
      ordinaryPreSchoolId: undefined,
      ordinaryElementarySchoolId: undefined,
      ordinaryMiddleSchoolId: undefined,
      ordinaryHighSchoolId: undefined,
      gradeYear: searchCondition.gradeYear === ''
        ? undefined : searchCondition.gradeYear as number,
      tagList: searchCondition.tagList,
      keyWord: searchCondition.keyWord,
      creditcardRegistered: searchCondition.creditcardRegistered === null
        ? undefined : searchCondition.creditcardRegistered,
      perPage: searchCondition.perPage,
      page: searchCondition.page,
      sort: searchCondition.sort,
      reservableCount: undefined,
      subjectIdList: undefined,
      studentIdList: undefined,
    };
    const response = await Axios.post('/api/Account/StudentSearch', request).catch((error) => {
      throw error;
    });
    response.data.accountList.forEach((account: any) => {
      if (typeof account.birthDay === 'string') {
        account.birthDay = new Date(account.birthDay + 'Z');
      }
    });
    return response.data;
  },
  async selectStudent({ dispatch, commit }: any, id: string) {
    commit('setLoading', true);
    const data = await dispatch('getStudent', id).catch((error: any) => {
      commit('setLoading', false);
      throw error;
    });
    commit('selectStudent', data);
  },
  async getStudent({ commit }: any, id: string) {
    const request: AccountRequest.StudentAccountRequest = {
      id,
      userId: undefined,
      lastName: undefined,
      firstName: undefined,
      lastNameKana: undefined,
      firstNameKana: undefined,
      genderType: undefined,
      birthDay: undefined,
      postalCode: undefined,
      address: undefined,
      phone: undefined,
      note: undefined,
      tutorIdList: undefined,
      ordinarySchoolId: undefined,
      ordinaryPreSchoolId: undefined,
      ordinaryElementarySchoolId: undefined,
      ordinaryMiddleSchoolId: undefined,
      ordinaryHighSchoolId: undefined,
      gradeYear: undefined,
      tagList: undefined,
      keyWord: undefined,
      creditcardRegistered: undefined,
      perPage: undefined,
      page: undefined,
      sort: undefined,
      reservableCount: undefined,
      subjectIdList: undefined,
      studentIdList: undefined,
    };
    const response = await Axios.post('/api/Account/StudentDetail', request).catch((error) => {
      throw error;
    });
    if (typeof response.data.birthDay === 'string') {
      response.data.birthDay = new Date(response.data.birthDay + 'Z');
    }
    return response.data;
  },
  async resetStudent({ commit }: any, student: Student) {
    const request: AccountRequest.StudentAccountRequest = {
      id: student.id,
      userId: undefined,
      lastName: undefined,
      firstName: undefined,
      lastNameKana: undefined,
      firstNameKana: undefined,
      genderType: undefined,
      birthDay: undefined,
      postalCode: undefined,
      address: undefined,
      phone: undefined,
      note: undefined,
      tutorIdList: undefined,
      ordinarySchoolId: undefined,
      ordinaryPreSchoolId: undefined,
      ordinaryElementarySchoolId: undefined,
      ordinaryMiddleSchoolId: undefined,
      ordinaryHighSchoolId: undefined,
      gradeYear: undefined,
      tagList: undefined,
      keyWord: undefined,
      creditcardRegistered: undefined,
      perPage: undefined,
      page: undefined,
      sort: undefined,
      reservableCount: undefined,
      subjectIdList: undefined,
      studentIdList: undefined,
    };
    const response = await Axios.post(
      '/api/Account/ResetStudent',
      request,
      { responseType: 'blob' }
    ).catch((error) => {
      throw new Error('案内資料の再発行に失敗しました。');
    });

    downloadBrochure(response, student);

    return response.data;
  },
  async createStudent({ commit }: any, student: Student) {
    if (!student || !student.lastName || !student.firstName) {
      throw new Error('氏名を入力してください。');
    }
    if (!student.lastNameKana || !student.firstNameKana) {
      throw new Error('カナ氏名を入力してください。');
    }
    student.tagList = createTagList(student.tagList);
    const request: AccountRequest.StudentAccountRequest = {
      id: undefined,
      userId: student.userId,
      lastName: student.lastName,
      firstName: student.firstName,
      lastNameKana: student.lastNameKana,
      firstNameKana: student.firstNameKana,
      genderType: student.genderType,
      birthDay: student.birthDay,
      postalCode: student.postalCode,
      address: student.address,
      phone: student.phone,
      note: student.note,
      tutorIdList: student.tutorList.map((tutor) => {
        return tutor.id;
      }),
      ordinarySchoolId: undefined,
      ordinaryPreSchoolId: student.ordinaryPreSchoolId,
      ordinaryElementarySchoolId: student.ordinaryElementarySchoolId,
      ordinaryMiddleSchoolId: student.ordinaryMiddleSchoolId,
      ordinaryHighSchoolId: student.ordinaryHighSchoolId,
      gradeYear: student.gradeYear,
      tagList: student.tagList,
      keyWord: undefined,
      creditcardRegistered: undefined,
      perPage: undefined,
      page: undefined,
      sort: undefined,
      reservableCount: student.reservableCount,
      subjectIdList: student.subjectList.map((subject) => {
        return subject.id;
      }),
      studentIdList: undefined,
    };
    const response = await Axios.post(
      '/api/Account/StudentCreate',
      request,
      { responseType: 'blob' }
    ).catch(async (error) => {
      if (!!error.response.data) {
        let data: any | null = null;
        try {
          const resText = await new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.addEventListener('abort', reject);
            reader.addEventListener('error', reject);
            reader.addEventListener('loadend', () => {
              resolve(reader.result!);
            });
            reader.readAsText(error.response.data);
          });
          data = JSON.parse(resText as string);
        } catch (err) {
          //
        }
        if (!data) {
          throw new Error('生徒の登録に失敗しました。');
        } else {
          throw new Error(data.UserId);
        }
      } else {
        throw new Error('生徒の登録に失敗しました。');
      }
    });

    downloadBrochure(response, student);

    return response.data;
  },
  async editStudent({ commit }: any, student: Student) {
    if (!student || !student.id) {
      throw new Error('変更する生徒を選択してください。');
    }
    if (!student || !student.lastName || !student.firstName) {
      throw new Error('氏名を入力してください。');
    }
    if (!student.lastNameKana || !student.firstNameKana) {
      throw new Error('カナ氏名を入力してください。');
    }
    student.tagList = createTagList(student.tagList);
    const request: AccountRequest.StudentAccountRequest = {
      id: student.id,
      userId: student.userId,
      lastName: student.lastName,
      firstName: student.firstName,
      lastNameKana: student.lastNameKana,
      firstNameKana: student.firstNameKana,
      genderType: student.genderType,
      birthDay: student.birthDay,
      postalCode: student.postalCode,
      address: student.address,
      phone: student.phone,
      note: student.note,
      tutorIdList: student.tutorList.map((tutor) => {
        return tutor.id;
      }),
      ordinarySchoolId: undefined,
      ordinaryPreSchoolId: student.ordinaryPreSchoolId,
      ordinaryElementarySchoolId: student.ordinaryElementarySchoolId,
      ordinaryMiddleSchoolId: student.ordinaryMiddleSchoolId,
      ordinaryHighSchoolId: student.ordinaryHighSchoolId,
      gradeYear: student.gradeYear,
      tagList: student.tagList,
      keyWord: undefined,
      creditcardRegistered: undefined,
      perPage: undefined,
      page: undefined,
      sort: undefined,
      reservableCount: student.reservableCount,
      subjectIdList: student.subjectList.map((subject) => {
        return subject.id;
      }),
      studentIdList: undefined,
    };
    const response = await Axios.post('/api/Account/StudentEdit', request).catch((error) => {
      throw new Error('生徒の変更に失敗しました。');
    });
    commit('changeStudent', student);
    return response.data;
  },
  async deleteStudent({ commit }: any, id: string) {
    if (!id) {
      throw new Error('削除する生徒を選択してください。');
    }
    const request: AccountRequest.StudentAccountRequest = {
      id,
      userId: undefined,
      lastName: undefined,
      firstName: undefined,
      lastNameKana: undefined,
      firstNameKana: undefined,
      genderType: undefined,
      birthDay: undefined,
      postalCode: undefined,
      address: undefined,
      phone: undefined,
      note: undefined,
      tutorIdList: undefined,
      ordinarySchoolId: undefined,
      ordinaryPreSchoolId: undefined,
      ordinaryElementarySchoolId: undefined,
      ordinaryMiddleSchoolId: undefined,
      ordinaryHighSchoolId: undefined,
      gradeYear: undefined,
      tagList: undefined,
      keyWord: undefined,
      creditcardRegistered: undefined,
      perPage: undefined,
      page: undefined,
      sort: undefined,
      reservableCount: undefined,
      subjectIdList: undefined,
      studentIdList: undefined,
    };
    const response = await Axios.post('/api/Account/StudentDelete', request).catch((error) => {
      throw new Error('生徒の削除に失敗しました。');
    });
    commit('removeStudent', id);
    return response.data;
  },
  async bulkDeleteStudents({ commit }: any, targetStudents: string[]) {
    if (!targetStudents || targetStudents.length === 0) {
      throw new Error('一括削除する生徒を選択してください。');
    }
    const request: AccountRequest.BulkDeleteStudentsRequest = {
      studentIdList: targetStudents
    };
    const response = await Axios.post('/api/Account/BulkDeleteStudents', request).catch((error) => {
      throw new Error('生徒の一括削除に失敗しました。');
    });
    return response.data;
  },
  async downloadReservationList({ commit, dispatch }: any, searchCondition: SearchCondition) {
    const request: AccountRequest.StudentReservationListRequest = {
      id: undefined,
      userId: undefined,
      lastName: undefined,
      firstName: undefined,
      lastNameKana: undefined,
      firstNameKana: undefined,
      genderType: undefined,
      birthDay: undefined,
      postalCode: undefined,
      address: undefined,
      phone: undefined,
      note: undefined,
      tutorIdList: searchCondition.tutorList,
      ordinarySchoolId: searchCondition.ordinarySchoolId === ''
        ? undefined : searchCondition.ordinarySchoolId as string,
      ordinaryPreSchoolId: undefined,
      ordinaryElementarySchoolId: undefined,
      ordinaryMiddleSchoolId: undefined,
      ordinaryHighSchoolId: undefined,
      gradeYear: searchCondition.gradeYear === ''
        ? undefined : searchCondition.gradeYear as number,
      tagList: searchCondition.tagList,
      keyWord: searchCondition.keyWord,
      creditcardRegistered: searchCondition.creditcardRegistered === null
        ? undefined : searchCondition.creditcardRegistered,
      perPage: searchCondition.perPage,
      page: -1,
      sort: searchCondition.sort,
      reservableCount: undefined,
      subjectIdList: undefined,
      schoolName: searchCondition.schoolName,
      studentIdList: searchCondition.studentIdList,
    };

    const response = await Axios.post('/api/Account/StudentReservationDownLoad',
        request,
        { responseType: 'blob' }
    ).catch((error) => {
      throw new Error('予約数出力に失敗しました。');
    });

    let fileName = '';
    const disposition = response.headers['content-disposition'];
    if (disposition && disposition.indexOf('attachment') !== -1) {
      const matches = /filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/.exec(disposition);
      if (matches != null && matches[1]) {
        fileName = decodeURI(matches[1].replace(/['"]/g, ''));
      }
    }

    if (fileName === '') {
      fileName = '今月の予約数.xlsx';
    }

    if (!(window.navigator as any).msSaveOrOpenBlob) {
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', fileName);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } else {
      const url = (window.navigator as any).msSaveOrOpenBlob(new Blob([response.data]), fileName);
    }

    return true;
  },
};

const mapMutations = {
  setLoading(state: State, loading: boolean) {
    state.loading = loading;
  },
  searchStudents(state: State, data: AccountResponse.StudentAccountListResponse) {
    state.countAll = data.countAll;
    state.students = data.accountList;
    state.loading = false;
  },
  selectStudent(state: State, data: AccountResponse.StudentAccountDetailResponse) {
    state.selectedStudent = data;
    state.loading = false;
  },
  changeStudent(state: State, student: Student) {
    const findIndex = state.students.findIndex((value) => {
      return (value.id === student.id);
    });
    if (findIndex >= 0) {
      state.students.splice(findIndex, 1, student);
    }
  },
  removeStudent(state: State, id: string) {
    const findIndex = state.students.findIndex((value) => {
      return (value.id === id);
    });
    if (findIndex >= 0) {
      state.students.splice(findIndex, 1);
    }
  },
  initSelectedStudent(state: State) {
    state.selectedStudent = {
      id: '',
      userId: '',
      lastName: '',
      firstName: '',
      lastNameKana: '',
      firstNameKana: '',
      authority: UserTypes.Student,
      schoolId: '',
      schoolName: '',
      ordinaryPreSchoolId: '',
      ordinaryPreSchoolName: '',
      ordinaryElementarySchoolId: '',
      ordinaryElementarySchoolName: '',
      ordinaryMiddleSchoolId: '',
      ordinaryMiddleSchoolName: '',
      ordinaryHighSchoolId: '',
      ordinaryHighSchoolName: '',
      genderType: GenderTypes.None,
      genderTypeName: getGenderTypeName(GenderTypes.None),
      birthDay: null,
      postalCode: '',
      address: '',
      phone: '',
      schoolType: SchoolTypes.ElementarySchool,
      schoolTypeName: '',
      gradeYear: GradeYears.ElementarySchool1,
      gradeYearName: '',
      tutorList: new Array<Person>(),
      tagList: new Array<Tag>(),
      note: '',
      availableFunctionbit: 0,
      availableFunctionTypes: new Array<FunctionTypes>(),
      reservableCount: 0,
      subjectList: new Array<Subject>(),
    };
  },
};

function createTagList(baseTagList: Tag[]): Tag[] {
  if (!baseTagList) {
    return new Array<Tag>();
  }
  return baseTagList.map((item: any) => {
    if (!item.id) {
      return { id: 0, name: item };
    } else {
      return item;
    }
  });
}

function downloadBrochure(response: AxiosResponse < any >, student: Student) {
  let fileName = '';
  const disposition = response.headers['content-disposition'];
  if (disposition && disposition.indexOf('attachment') !== -1) {
    const matches = /filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/.exec(disposition);
    if (matches != null && matches[1]) {
      fileName = decodeURI(matches[1].replace(/['"]/g, ''));
    }
  }
  if (fileName === '') {
    fileName = `案内資料_${student.userId}_${student.lastName}${student.firstName}.xlsx`;
  }

  if (!(window.navigator as any).msSaveOrOpenBlob) {
    const url = window.URL.createObjectURL(new Blob([response.data]));
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', fileName);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  } else {
    const url = (window.navigator as any).msSaveOrOpenBlob(new Blob([response.data]), fileName);
  }
}

export default {
  namespaced: true,
  state: mapState,
  actions: mapActions,
  mutations: mapMutations,
};
